import { ApexOptions } from "apexcharts";
import { formatDate } from "./dateTime";

export type DataType = {
  title?: string;
  value: number;
  percentage?: number;
  color?: string;
  totalCount?: number;
  data_timestamp: string | Date | number;
};

export type ChartData = {
  [key: string]: DataType[];
};

export const prepareChartData = (
  data: ChartData,
  chartType: "bar" | "line",
  dateType: "days" | "time" | "month"
) => {
  const location = window && window?.location?.href?.split("/");
  const fileName =
    (location && `${location[location.length - 1]} data`) || "data";
  const transformDataForChart = (data: DataType[]) => {
    const sortedData = [...data].sort(
      (a, b) =>
        new Date(a.data_timestamp).getTime() -
        new Date(b.data_timestamp).getTime()
    );

    const dateFormat =
      dateType === "month"
        ? "MMM YYYY"
        : dateType === "days"
        ? "D MMM"
        : "h:mm A";

    return {
      dates: sortedData.map((item) =>
        formatDate(item.data_timestamp, dateFormat)
      ),
      values: sortedData.map((item) => item.value)
    };
  };

  const collectedDataTransformed = transformDataForChart(data["data1"] || []);
  const missedDataTransformed = transformDataForChart(data["data2"] || []);

  const getLimitedData = (
    data: { dates: string[]; values: number[] },
    limit: number
  ) => {
    const slicedDates = data.dates.slice(0, limit);
    const slicedValues = data.values.slice(0, limit);
    return { dates: slicedDates, values: slicedValues };
  };

  const isMobile = window.innerWidth < 768;
  const limit = isMobile ? 4 : collectedDataTransformed.dates.length; // Limit to 4 on mobile or show all

  const collectedDataLimited = getLimitedData(collectedDataTransformed, limit);
  const missedDataLimited = getLimitedData(missedDataTransformed, limit);

  const series = [
    {
      name: "% Fetched",
      data: collectedDataLimited.values
    },
    {
      name: "% Missed",
      data: missedDataLimited.values
    }
  ];

  const commonOptions: ApexOptions = {
    chart: {
      zoom: { enabled: false },
      type: chartType,
      height: 350,
      toolbar: {
        offsetY: -45,
        tools: {
          download:
            '<img src="/assets/images/other/graphDownload.svg " class="graph-download-icon"> </img>'
        },
        show: true,
        export: {
          csv: {
            filename: fileName
          },
          svg: {
            filename: fileName
          },
          png: {
            filename: fileName
          }
        }
      }
    },
    yaxis: {
      tooltip: {
        enabled: false
      },
      labels: {
        formatter: (val) => `${val}%`,
        style: {
          fontSize: "12px",
          colors: ["#A3B2CF"]
        }
      }
    },

    xaxis: {
      tooltip: {
        enabled: false
      },
      categories: collectedDataLimited.dates,
      labels: {
        style: {
          fontSize: "12px",
          colors: collectedDataLimited.dates.map(() => "#A3B2CF")
        },
        format:
          dateType === "month"
            ? "MMM yyyy"
            : dateType === "days"
            ? "d MMM"
            : "h:mm TT"
      }
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      itemMargin: {
        horizontal: 20,
        vertical: 5
      }
    },
    tooltip: {
      followCursor: true,
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        return (
          '<div class="arrow_box">' +
          "<span>" +
          `${series[seriesIndex][dataPointIndex]}%` +
          "</span>" +
          "</div>"
        );
      }
    },
    noData: {
      text: "No data available for this time frame.",
      align: "center",
      verticalAlign: "middle",
      offsetX: 0,
      offsetY: 0,
      style: {
        color: "#0A3690",
        fontSize: "20px",
        fontFamily: "sans-serif"
      }
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 250
          },
          xaxis: {
            categories: collectedDataLimited.dates, // Show only the last 4 entries
            labels: {
              style: {
                fontSize: "8px"
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "8px"
              }
            }
          }
        }
      },
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 200
          },
          xaxis: {
            categories: collectedDataLimited.dates, // Show only the last 4 entries
            labels: {
              style: {
                fontSize: "6px"
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "6px"
              }
            }
          },
          legend: {
            fontSize: "10px",
            markers: {
              size: 4
            }
          },
          noData: {
            text: "No data available for this time frame.",
            align: "center",
            verticalAlign: "middle",
            offsetX: 0,
            offsetY: 0,
            style: {
              color: "#0A3690",
              fontSize: "10px",
              fontFamily: "sans-serif"
            }
          }
        }
      }
    ]
  };

  const barSpecificOptions: ApexOptions = {
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "50%"
      }
    },
    stroke: {
      colors: ["transparent"],
      width: 5
    },
    colors: ["#0A3690", "#B9CDF4"],
    grid: {
      show: true,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    dataLabels: {
      enabled: false
    }
  };

  const lineSpecificOptions: ApexOptions = {
    markers: {
      shape: "circle",
      size: 5
    },
    colors: ["#0A3690", "#02C9A8"],
    grid: {
      show: true,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    stroke: {
      curve: "smooth",
      width: 2
    }
  };

  const options = {
    ...commonOptions,
    ...(chartType === "bar" ? barSpecificOptions : lineSpecificOptions)
  };

  return { series, options };
};

export function formatString(input: string): string {
  if (input.includes("_")) {
    // Handle snake_case: Replace underscores with spaces and capitalize each word
    return input
      .split("_") // Split by underscore
      .map((word) => word.toUpperCase()) // Convert each word to uppercase
      .join(" "); // Join with spaces
  } else {
    // Handle camelCase: Insert space before each capital letter and capitalize each word
    return input
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before each capital letter
      .toUpperCase(); // Convert everything to uppercase
  }
}

export function camelCaseToTitleCase(input: string): string {
  return input
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize the first letter
    .toLowerCase() // Convert the entire string to lowercase
    .replace(/\b\w/g, (str) => str.toUpperCase()); // Capitalize first letter of each word
}

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return ""; // Handle empty string case
  return str.charAt(0).toUpperCase() + str.slice(1);
};
